import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FormSection from 'components/ui/form-section';
import FormLayoutGroup from 'components/ui/form-layout-group';
import ExcelTable from 'components/ui/excel-table';
import {
  AllRowsData,
  ECellValueDisplayMode,
  ECellValueType,
  RowData,
} from 'components/ui/excel-table/types';
import dayjs, { Dayjs } from 'dayjs';
import { isNil } from 'lodash';
import { ValueGetterParams } from 'ag-grid-community';
import { validateInstallmentDetails } from 'components/features/binding/contract-detail/installment-details/form';
import { FormInstance } from 'antd';
import { useWatch } from 'antd/es/form/Form';
import { ContractResponseDtoContractStatusEnum } from 'libs/pinnacle-sdk';

export interface InstallmentData {
  id: number;
  installmentPercent: number;
  totalAmountDue?: number;
  installmentDueDate: Dayjs;
}

interface InstallmentDetailsSectionProps {
  form: FormInstance;
  installmentData: InstallmentData[];
  setInstallmentData: (installmentData: InstallmentData[]) => void;
  setIsInstallmentsValid: (isInstallmentsValid: boolean) => void;
  disabled?: boolean;
}

const InstallmentDetailsSection: React.FC<InstallmentDetailsSectionProps> = ({
  form,
  installmentData,
  setInstallmentData,
  setIsInstallmentsValid,
  disabled = false,
}) => {
  const shareOfPremiumSigned = useWatch('shareOfPremiumSigned', form);
  const layerPremium = useWatch<string>('layerPremium', form);

  const installments = useWatch<boolean>('installments', form);

  const contractStatus = useWatch<ContractResponseDtoContractStatusEnum>(
    'contractStatus',
    form
  );

  const { t } = useTranslation();

  const columnDefs = useMemo(
    () => [
      {
        headerName: t('contractDetail.installmentPercent'),
        field: 'installmentPercent',
        type: ECellValueType.NUMERIC_COLUMN,
        displayMode: ECellValueDisplayMode.PERCENTAGE,
        editable: !disabled,
        precision: 0,
        minWidth: 335,
        flex: 1,
        sortable: false,
        resizable: true,
      },
      {
        headerName: t('contractDetail.fullAmount'),
        field: 'fullAmount',
        type: ECellValueType.NUMERIC_COLUMN,
        editable: false,
        minWidth: 335,
        flex: 1,
        sortable: false,
        valueGetter: (params: ValueGetterParams) =>
          !isNil(params.data.installmentPercent) && !isNil(layerPremium)
            ? (params.data.installmentPercent * Number(layerPremium)) / 100
            : null,
        resizable: true,
      },
      {
        headerName: t('contractDetail.shareOfPremiumSigned'),
        field: 'totalAmountDue',
        type: ECellValueType.NUMERIC_COLUMN,
        editable: false,
        minWidth: 335,
        flex: 1,
        sortable: false,
        valueGetter: (params: ValueGetterParams) =>
          !isNil(params.data.installmentPercent)
            ? (params.data.installmentPercent * shareOfPremiumSigned) / 100
            : null,
        resizable: true,
      },
      {
        headerName: t('contractDetail.installmentDueDate'),
        field: 'installmentDueDate',
        type: ECellValueType.DATE_COLUMN,
        editable: !disabled,
        minWidth: 335,
        flex: 1,
        sortable: false,
        resizable: true,
        disabledDate: (current: Dayjs) => {
          const today = dayjs().startOf('day');
          return current < today;
        },
      },
    ],
    [t, shareOfPremiumSigned, layerPremium, disabled]
  );

  const newRowData = useMemo(
    () => ({
      installmentPercent: undefined,
      totalAmountDue: undefined,
      installmentDueDate: dayjs(),
    }),
    []
  );

  const onDataChange = useCallback(
    async (
      affectedRow: RowData<InstallmentData>,
      rows: AllRowsData<InstallmentData>
    ) => {
      if (affectedRow.isError.strict) return;
      setInstallmentData(rows.data);
    },
    [setInstallmentData]
  );

  const onErrorChange = useCallback(
    (isError: { strict: boolean; soft: boolean }) => {
      if (!isError.soft && !isError.strict) {
        setIsInstallmentsValid(true);
      } else {
        setIsInstallmentsValid(false);
      }
    },
    [setIsInstallmentsValid]
  );

  const canDeleteRow = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (_: InstallmentData) => {
      return installmentData.length > 2;
    },
    [installmentData]
  );

  return (
    <FormSection title={t('contractDetail.installmentDetails')} collapsible>
      <FormLayoutGroup mdColumns={1}>
        <ExcelTable
          data={installmentData}
          columnDefs={columnDefs}
          rowKeyColName="id"
          includeIdRow
          includeAddRow={!disabled}
          includeDeleteCol={!disabled}
          tableContainerClassName="h-[33rem]"
          newRowData={newRowData}
          validateRow={validateInstallmentDetails(installments, contractStatus)}
          onDataChange={onDataChange}
          onErrorChange={onErrorChange}
          onDeleteRow={onDataChange}
          canDeleteRow={canDeleteRow}
        />
      </FormLayoutGroup>
    </FormSection>
  );
};

export default InstallmentDetailsSection;
